import { useEffect, useState } from 'react';
import { Tooltip, Form } from 'antd';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { listDepartments } from 'services/api/department/department';
import { useQuery } from '@tanstack/react-query';
import checkedSvg from '../assets/checked-blue.svg';

/**
 * 在卖家管理->店铺管理->添加店铺的场景中使用，适用shopee, lazada, tiktok
 */
type DepartmentItemSelectProps = {
  value?: number[];
  onChange?: (value: number[]) => void;
  showRequiredMark?: boolean;
  onReady?: (value: {
    list?: StoreHomeV2.Department[];
    onlyDefault: boolean;
    noDepartment: boolean;
  }) => void;
  platform: General.Platform;
};

type DepartmentListProps = Omit<DepartmentItemSelectProps, 'showRequiredMark'> & {
  dataSource?: StoreHomeV2.Department[];
};

function DepartmentList(props: DepartmentListProps) {
  const { value = [], onChange, dataSource: list = [] } = props;
  const [departments, setDepartments] = useState<number[]>(value);
  const { t } = useTranslate();

  useEffect(() => {
    onChange?.(departments);
  }, [departments, onChange]);

  useEffect(() => {
    if (list.length === 0) {
      return;
    }
    const defaultId = list.find((item) => item.isDefault)?.id;
    // 默认选中 <默认部门>
    if (defaultId && !departments?.includes(defaultId)) {
      setDepartments([...departments, defaultId]);
    }
    // 如果没有默认部门，则默认选中第一个
    if (!defaultId && departments?.length === 0) {
      setDepartments([list[0].id]);
    }
  }, [list, departments]);

  return (
    <>
      {list.map((item) => {
        if (item.isDefault) {
          return (
            <Tooltip
              key={item.id}
              trigger="hover"
              title={t('authShop.authAutoDepartmentTip')}
              placement="bottomLeft"
              arrowPointAtCenter
            >
              <DepartmentTag className="checked disabled">
                {item.name === '客服部' ? t('department.客服部') : item.name}(
                {t('authShop.defaultTag')})
              </DepartmentTag>
            </Tooltip>
          );
        } else {
          return (
            <DepartmentTag
              key={item.id}
              className={departments?.includes(item.id) ? 'checked' : ''}
              onClick={() => {
                if (departments?.includes(item.id)) {
                  departments.length > 1 &&
                    setDepartments(departments.filter((i) => i !== item.id));
                } else {
                  setDepartments([...departments, item.id]);
                }
              }}
            >
              {item.name}
            </DepartmentTag>
          );
        }
      })}
    </>
  );
}

function DepartmentItemSelect(props: DepartmentItemSelectProps) {
  const { value, platform, onChange, showRequiredMark = true, onReady } = props;
  const { data } = useQuery({
    queryKey: ['departmentList', platform === 'tiktok_us' ? 2 : 1] as const,
    queryFn: async ({ queryKey }) => {
      const [, type] = queryKey;
      const response = await listDepartments({
        type,
      });
      const list = (response?.data?.data?.departments || []).sort((a) => (a.isDefault ? -1 : 1));
      const onlyDefault = list.length === 1 && list[0].isDefault;
      const noDepartment = list.length === 0;
      return {
        list,
        onlyDefault,
        noDepartment,
      };
    },
    onSuccess(data) {
      onReady?.(data);
    },
  });

  const { t } = useTranslate();

  return data?.onlyDefault || data?.noDepartment ? null : (
    <Form.Item
      label={t('authShop.chooseDepartmentLabel')}
      name="departmentIds"
      required={showRequiredMark}
      className={css`
        .ant-form-item-label {
          font-weight: 500;
          width: 100%;
          text-align: left;
        }
      `}
    >
      <DepartmentList
        platform={platform}
        dataSource={data?.list}
        value={value}
        onChange={onChange}
      />
    </Form.Item>
  );
}

const DepartmentTag = styled.div`
  display: inline-block;
  height: 32px;
  line-height: 32px;
  padding: 0 10px 0 8px;
  margin-bottom: 16px;
  margin-right: 16px;
  border: 1px solid rgba(211, 215, 224, 1);
  cursor: pointer;

  &:hover,
  &.checked {
    border-color: var(--ant-primary-color);
    color: var(--ant-primary-color);
  }
  &.checked {
    background: url(${checkedSvg}) no-repeat right bottom;
  }
  &.disabled {
    cursor: not-allowed;
    background-color: #f0f2f5;
    color: #515359;
  }
`;

export default DepartmentItemSelect;
